import {HttpErrorResponse} from '@angular/common/http';
import {hasUserRevision, userProfileErrorKey, USER_PROFILE_ERROR} from 'app/services/user-profile-snapshot';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbTabset } from '@ng-bootstrap/ng-bootstrap';
import { GradeGrowthComponent } from '../../common/grade-growth/grade-growth.component';
import {GradePromotionEvent} from '../../common/grade-growth/grade-growth.model';
import {DealPayrollUserSettingsComponent} from './deal-payroll-user-settings/deal-payroll-user-settings.component';
import { AssetProfileIssueComponent } from '../../common/employee-assets/asset-profile-issue.component';
import { HttpResponse } from '@angular/common/http';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/timer';
import 'rxjs/add/operator/takeUntil';
import { User } from '../../../models/user.model';
import { UserPayrollSettingsResponse } from '../../../models/deal-payroll.model';
import { GroupContext } from '../../../models/group-context.model';
import { AppConstants } from './app.constants';
import {
  UserProfile,
  ViewUserProfileClothingStateView,
  ViewUserProfileLearningWeight
} from './view-user-profile.types';
import {
  PROFILE_SCHEDULE_ENABLED_STATUS,
  PROFILE_STOREKEEPER_ROLE,
  PROFILE_INITIAL_LEARNING_WEIGHT,
  PROFILE_NUMBER_TYPE
} from './view-user-profile.constants';
import { ViewUserProfileClothingCoordinator } from './view-user-profile-clothing.coordinator';
import { ViewUserProfileClothingGateway } from './view-user-profile-clothing.gateway';
import { ViewUserProfileClothingStore } from './view-user-profile-clothing.store';
import { ViewUserProfileClothingUi } from './view-user-profile-clothing.ui';
import { ViewUserProfileGateway } from './view-user-profile.gateway';
import { ViewUserProfileFeedbackService } from './view-user-profile-feedback.service';
import { ViewUserProfilePayrollFacade } from './view-user-profile-payroll.facade';
import { ViewUserProfilePayrollGateway } from './view-user-profile-payroll.gateway';
import {profileSalaryAmount} from './view-user-profile-payroll.presentation';
import {salaryDescriptionText} from 'app/models/salary-description';

const PROFILE_NO_ERROR = '';
const PROFILE_INITIAL_SEQUENCE = 0;
const PROFILE_CONTEXT_ERROR = 'GROUP_CONTEXT_LOAD_ERROR';
const PROFILE_RENDER_DELAY = 0;
const PROFILE_GRADE_TAB = 'employee-grade';
const PROFILE_BOOLEAN_TYPE = 'boolean';

@Component({
  selector: 'app-view-user-profile',
  templateUrl: './view-user-profile.component.html',
  styleUrls: ['./view-user-profile.component.css'],
  providers: [
    ViewUserProfileGateway,
    ViewUserProfileFeedbackService,
    ViewUserProfileClothingGateway,
    ViewUserProfileClothingStore,
    ViewUserProfileClothingUi,
    ViewUserProfileClothingCoordinator,
    ViewUserProfilePayrollGateway,
    ViewUserProfilePayrollFacade
  ]
})
export class ViewUserProfileComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('profileTabs') private profileTabs: NgbTabset;
  @ViewChild(GradeGrowthComponent) private gradeGrowth: GradeGrowthComponent;
  @ViewChild(DealPayrollUserSettingsComponent) private payrollSettings: DealPayrollUserSettingsComponent;
  @ViewChild(AssetProfileIssueComponent) assetsPanel: AssetProfileIssueComponent;
  isLoaded = false;
  user: UserProfile;
  notFound = false;
  request = false;
  isAdmin = false;
  isHead = false;
  isUser = false;
  isStorekeeper = false;
  userActive: User;
  userId: string;
  errorMessage = false;
  visible = false;
  isCheckTimetablePF: boolean;
  isCheckTimetable: boolean;
  ountRecyclingB: boolean;
  checkId = true;
  learningSumWeight = PROFILE_INITIAL_LEARNING_WEIGHT;
  isLearningPointsLoaded = false;
  learningPointsError = false;
  readonly clothingState: ViewUserProfileClothingStateView;
  groupContext: GroupContext = null;
  assignedEmployeeServices = false;
  private groupList: Object[];
  profileSaving = false;
  profileLoading = false;
  profileRequiresReload = false;
  profileSaved = false;
  profileErrorKey = PROFILE_NO_ERROR;
  private profileRequestSequence = PROFILE_INITIAL_SEQUENCE;
  private profileContextSequence = PROFILE_INITIAL_SEQUENCE;
  private payrollRefreshPending = false;
  private refreshingGradeEmployeeId: string = null;
  private requestedGradeEmployeeId: string = null;
  private destroy$ = new Subject<void>();

  constructor(
    private gateway: ViewUserProfileGateway,
    public readonly clothing: ViewUserProfileClothingCoordinator,
    public readonly payroll: ViewUserProfilePayrollFacade
  ) {
    this.clothingState = clothing.viewState();
  }

  ngOnInit(): void {
    this.initializeUserRoles();
    this.payroll.promotionCommitted$.takeUntil(this.destroy$).subscribe(event => this.gradePromotionCommitted(event));
    this.requestedGradeEmployeeId = this.gateway.getProfileFragment() === PROFILE_GRADE_TAB ? this.getUserId() : null;
    this.getUserInfo();
  }

  ngOnDestroy(): void {
    this.refreshingGradeEmployeeId = null;
    this.requestedGradeEmployeeId = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    Observable.timer(PROFILE_RENDER_DELAY).takeUntil(this.destroy$).subscribe(() => this.gateway.resetScroll());
  }

  private openRequestedGradeTab(): void {
    const employeeId = this.requestedGradeEmployeeId;
    if (!employeeId || !this.user || this.user.id !== employeeId || !this.canReadGradeGrowth()) { return; }
    Observable.timer(PROFILE_RENDER_DELAY).takeUntil(this.destroy$).subscribe(() => {
      if (this.requestedGradeEmployeeId !== employeeId || this.getUserId() !== employeeId ||
        !this.profileTabs || !this.canReadGradeGrowth()) { return; }
      this.profileTabs.select(PROFILE_GRADE_TAB);
      if (this.profileTabs.activeId === PROFILE_GRADE_TAB) { this.requestedGradeEmployeeId = null; }
    });
  }

  trackByRole(index: number, role: string): string {
    return role;
  }

  showMobileProfile(): boolean {
    return !!this.user && !this.notFound;
  }

  showStorekeeperMobileProfile(): boolean {
    return this.showMobileProfile();
  }

  open(): void {
    this.payroll.openEmployment(this.getUserId(), this.user);
  }

  canShowPayrollSettings(): boolean {
    return this.hasProfileContext() && this.groupContext.canReadPayrollSettings;
  }

  get salaryAmount(): number | null {
    return profileSalaryAmount(this.user && this.user.salary);
  }

  get salaryDescription(): string {
    return salaryDescriptionText(this.user && this.user.descriptionSalary);
  }

  canReadGradeGrowth(): boolean {
    return !!this.user && (this.user.id === this.userActive.id ||
      this.hasProfileContext() && this.groupContext.canReadSalaryReport);
  }

  canShowGradeTab(): boolean {
    return this.canReadGradeGrowth() || !!this.user && this.refreshingGradeEmployeeId === this.user.id;
  }

  canShowClothingTab(): boolean {
    return !!this.user && (this.user.id === this.userActive.id ||
      this.visible && (this.isHead || this.isAdmin) || this.isStorekeeper || this.assignedEmployeeServices);
  }

  canReadGradePromotion(): boolean {
    return !!this.user && !!this.userActive && this.user.id !== this.userActive.id &&
      this.canReadGradeGrowth() && (this.isHead || this.isAdmin);
  }

  gradePromotionCommitted(event: GradePromotionEvent): void {
    if (!event || !this.user || event.employeeId !== this.user.id) { return; }
    if (this.payrollSettings) { this.payrollSettings.reload(); }
    this.refreshPayrollProfile();
  }

  probationChanged(): void {
    if (this.payrollSettings) { this.payrollSettings.reload(); }
    if (this.gradeGrowth) { this.gradeGrowth.refresh(); }
  }

  private hasProfileContext(): boolean {
    return !!this.user && !!this.groupContext && this.groupContext.groupId === this.user.mainGroup &&
      typeof this.groupContext.canManageEmployeeProfiles === PROFILE_BOOLEAN_TYPE;
  }

  isDealPayrollMethod(): boolean {
    return this.payroll.isDealMethod(this.user);
  }

  getPayrollCalculationLabelKey(): string {
    return this.payroll.calculationLabelKey(this.user);
  }

  applyDealPayrollSettings(settings: UserPayrollSettingsResponse): void {
    if (!this.user || !settings || settings.userId !== this.user.id) { return; }
    this.refreshPayrollProfile();
  }

  private refreshPayrollProfile(): void {
    this.profileRequiresReload = true;
    this.payrollRefreshPending = true;
    if (!this.profileSaving) { this.profileLoading = false; this.reloadUserProfile(); }
    if (this.gradeGrowth) { this.gradeGrowth.refresh(); }
  }

  postiruemOvertime(tumbler: boolean): void {
    if (this.profileWriteDisabled || !this.canShowPayrollSettings()) { return; }
    this.profileSaving = true;
    this.profileSaved = false;
    this.gateway.updateOvertime(this.user.id, tumbler).takeUntil(this.destroy$).subscribe(() => {
      this.profileSaving = false;
      this.loadUserData();
    }, error => this.rejectProfileWrite(error));
  }

  changeTime(status: string): void {
    if (this.profileWriteDisabled) { return; }
    const isOn = status === PROFILE_SCHEDULE_ENABLED_STATUS;
    this.isCheckTimetable = isOn;
    this.submit(isOn);
  }

  biometrics(): void {
    this.clothing.openBiometrics(() => this.getUserInfo());
  }

  openEditPos(): void {
    this.payroll.openSalary(this.user, this.isAdmin, () => this.getUserInfo());
  }

  openClothes(): void {
    this.clothing.openClothes(() => this.clothing.loadLegacyClothes(this.getUserId()));
  }

  private initializeUserRoles(): void {
    this.userActive = this.gateway.activeUser();
    this.isAdmin = this.userActive.roles.includes(AppConstants.ROLES.ADMIN);
    this.isHead = this.userActive.roles.includes(AppConstants.ROLES.HEAD);
    this.isUser = this.userActive.roles.includes(AppConstants.ROLES.USER);
    this.isStorekeeper = this.userActive.roles.includes(AppConstants.ROLES.STOREKEEPER)
      || this.userActive.roles.includes(PROFILE_STOREKEEPER_ROLE);
  }

  private loadUserData(): void { this.reloadUserProfile(); }

  get profileWriteDisabled(): boolean {
    return !this.visible || this.profileSaving || this.profileLoading || this.profileRequiresReload ||
      !hasUserRevision(this.user) || !Array.isArray(this.groupList);
  }

  reloadUserProfile(): void {
    if (this.profileSaving || this.profileLoading) { return; }
    if (!this.visible || !this.user || this.user.id !== this.getUserId()) { this.getUserInfo(); return; }
    this.payrollRefreshPending = this.profileSaved = false;
    const userId = this.getUserId();
    const sequence = ++this.profileRequestSequence;
    this.profileLoading = true;
    this.gateway.loadEditableProfile(userId).takeUntil(this.destroy$).subscribe(snapshot => {
      if (sequence !== this.profileRequestSequence || userId !== this.getUserId()) { return; }
      this.profileLoading = this.profileRequiresReload = false;
      this.profileErrorKey = PROFILE_NO_ERROR;
      this.groupList = snapshot.groupsWithPositions;
      this.updateUserSettings(snapshot.user);
      this.isLoaded = true;
      this.loadHeadVisibility(new HttpResponse<UserProfile>({body: snapshot.user, status: AppConstants.HTTP_STATUS.OK}));
    }, () => { if (sequence === this.profileRequestSequence) { this.rejectProfileLoad(); } });
  }

  private rejectProfileLoad(): void {
    this.refreshingGradeEmployeeId = null;
    this.profileLoading = false;
    this.profileRequiresReload = true;
    this.profileErrorKey = USER_PROFILE_ERROR.reload;
  }

  private updateUserSettings(userData: UserProfile): void {
    this.user = userData;
    this.profileRequiresReload = false;
    this.profileErrorKey = PROFILE_NO_ERROR;
    this.isCheckTimetablePF = userData.payrollMethod;
    this.isCheckTimetable = userData.checkTimetable;
    this.ountRecyclingB = userData.acceptOvertime;
  }

  private loadGroupList(): void {
    const source = this.user;
    if (!hasUserRevision(source)) { this.rejectProfileLoad(); return; }
    const sequence = ++this.profileRequestSequence;
    this.profileLoading = true;
    this.gateway.loadUserGroups(source.id).takeUntil(this.destroy$).subscribe(response => {
      if (sequence !== this.profileRequestSequence || this.user !== source) { return; }
      if (response.status !== AppConstants.HTTP_STATUS.OK || !response.body || !Array.isArray(response.body.groupsWithPositions)) {
        this.rejectProfileLoad(); return;
      }
      this.groupList = response.body.groupsWithPositions;
      this.profileLoading = false;
    }, () => { if (sequence === this.profileRequestSequence) { this.rejectProfileLoad(); } });
  }

  private submit(checkTimetable: boolean): void {
    if (this.profileWriteDisabled) { return; }
    const source = this.user;
    const submitted: UserProfile = JSON.parse(JSON.stringify(Object.assign({}, source, {checkTimetable: checkTimetable})));
    const groups: Object[] = JSON.parse(JSON.stringify(this.groupList));
    this.profileSaving = true;
    this.profileSaved = false;
    this.gateway.updateUser(submitted, groups).takeUntil(this.destroy$).subscribe(
      response => this.acceptProfileWrite(response, source, submitted), error => this.rejectProfileWrite(error));
  }

  private acceptProfileWrite(response: HttpResponse<UserProfile>, source: UserProfile, submitted: UserProfile): void {
    this.profileSaving = false;
    if (response.status !== AppConstants.HTTP_STATUS.OK || !hasUserRevision(response.body) ||
      response.body.id !== submitted.id || this.user !== source || source.userRevision !== submitted.userRevision) {
      this.rejectProfileWrite(); return;
    }
    this.updateUserSettings(response.body);
    this.profileRequiresReload = false;
    this.profileSaved = true;
    this.profileErrorKey = PROFILE_NO_ERROR;
    if (this.payrollRefreshPending) { this.reloadUserProfile(); }
  }

  private rejectProfileWrite(error?: HttpErrorResponse): void {
    this.refreshingGradeEmployeeId = null;
    this.profileSaving = this.profileSaved = false;
    this.payrollRefreshPending = false;
    this.profileRequiresReload = true;
    this.profileErrorKey = userProfileErrorKey(error);
  }

  private getUserInfo(): void {
    if (this.profileSaving || this.profileLoading) { return; }
    const sequence = ++this.profileRequestSequence;
    const userId = this.getUserId();
    if (this.user && this.user.id !== userId) { this.user = null; this.isLoaded = false; }
    this.resetProfileAccess();
    this.profileLoading = true;
    this.gateway.loadUser(userId).takeUntil(this.destroy$).subscribe(response => {
      if (sequence !== this.profileRequestSequence || userId !== this.getUserId()) { return; }
      if (response.status === AppConstants.HTTP_STATUS.OK && (!response.body || response.body.id !== userId)) {
        this.rejectProfileLoad(); return;
      }
      this.profileLoading = false;
      this.handleUserInfoResponse(response);
    }, () => { if (sequence === this.profileRequestSequence) { this.rejectProfileLoad(); } });
  }

  private getUserId(): string {
    return this.gateway.routeUserId();
  }

  private handleUserInfoResponse(response: HttpResponse<UserProfile>): void {
    this.errorMessage = false;
    this.userId = this.getUserId();
    this.user = response.body;
    this.isLoaded = true;
    switch (response.status) {
      case AppConstants.HTTP_STATUS.OK:
        this.handleSuccessResponse(response);
        break;
      case AppConstants.HTTP_STATUS.NO_CONTENT:
        this.setUserState(true, false);
        break;
      case AppConstants.HTTP_STATUS.PRECONDITION_FAILED:
        this.setUserState(false, true);
        break;
    }
  }

  private handleSuccessResponse(response: HttpResponse<UserProfile>): void {
    this.updateUserSettings(response.body);
    this.loadHeadVisibility(response);
    this.checkId = this.userId === this.userActive.id ? this.isAdmin : true;
    this.setUserState(false, false);
    this.openRequestedGradeTab();
  }

  private loadLearningPoints(): void {
    if (!this.canShowLearningPoints()) {
      return;
    }
    this.isLearningPointsLoaded = false;
    this.learningPointsError = false;
    const ownProfile = this.userId === this.userActive.id;
    const source = this.user;
    const sequence = this.profileContextSequence;
    this.gateway.loadLearningWeight(this.userId, ownProfile).takeUntil(this.destroy$).subscribe(
      response => { if (this.user === source && sequence === this.profileContextSequence) { this.handleLearningPointsSuccess(response.body); } },
      () => { if (this.user === source && sequence === this.profileContextSequence) { this.handleLearningPointsError(); } }
    );
  }

  private handleLearningPointsSuccess(body: ViewUserProfileLearningWeight): void {
    const weight = body && body.sumWeight;
    this.learningSumWeight = typeof weight === PROFILE_NUMBER_TYPE
      ? weight
      : PROFILE_INITIAL_LEARNING_WEIGHT;
    this.isLearningPointsLoaded = true;
  }

  private handleLearningPointsError(): void {
    this.learningSumWeight = PROFILE_INITIAL_LEARNING_WEIGHT;
    this.isLearningPointsLoaded = true;
    this.learningPointsError = true;
  }

  private canShowLearningPoints(): boolean {
    return !!this.user && (this.user.id === this.userActive.id ||
      this.hasProfileContext() && this.groupContext.canManageEmployeeProfiles === true);
  }

  private loadHeadVisibility(response: HttpResponse<UserProfile>): void {
    this.refreshingGradeEmployeeId = this.profileTabs && this.profileTabs.activeId === PROFILE_GRADE_TAB
      ? response.body.id : null;
    this.resetProfileAccess();
    const source = response.body;
    const sequence = this.profileContextSequence;
    if (!source.mainGroup) { this.loadPermittedProfileDetails(); return; }
    this.gateway.loadGroupContext(source.mainGroup).takeUntil(this.destroy$).subscribe(contextResponse => {
      if (this.user !== source || source.id !== this.getUserId() || sequence !== this.profileContextSequence) { return; }
      this.acceptProfileContext(contextResponse);
    }, () => { if (this.user === source && sequence === this.profileContextSequence) { this.rejectProfileContext(); } });
  }

  private acceptProfileContext(response: HttpResponse<GroupContext>): void {
    const context = response.body;
    if (response.status !== AppConstants.HTTP_STATUS.OK || !context || context.groupId !== this.user.mainGroup) {
      this.rejectProfileContext(); return;
    }
    this.groupContext = context;
    this.visible = context.canManageEmployeeProfiles === true ||
      this.user.id === this.userActive.id && (this.isHead || this.isAdmin);
    this.loadPermittedProfileDetails();
  }

  private rejectProfileContext(): void {
    this.profileRequiresReload = true;
    this.profileErrorKey = PROFILE_CONTEXT_ERROR;
    this.loadPermittedProfileDetails();
  }

  private loadPermittedProfileDetails(): void {
    this.refreshingGradeEmployeeId = null;
    this.loadProfileClothing();
    this.loadAssignedEmployeeServices();
    if (this.visible) { this.loadGroupList(); }
    if (this.canReadGradeGrowth()) { this.payroll.loadSalaryContext(this.user.id, this.user); }
    this.loadLearningPoints();
    this.openRequestedGradeTab();
  }

  private resetProfileAccess(): void {
    this.profileContextSequence++;
    this.groupContext = null;
    this.visible = false;
    this.assignedEmployeeServices = false;
    this.groupList = null;
    this.isLearningPointsLoaded = false;
    this.learningPointsError = false;
    this.learningSumWeight = PROFILE_INITIAL_LEARNING_WEIGHT;
    this.loadProfileClothing();
  }

  private loadProfileClothing(): void {
    this.clothing.load({userId: this.user && this.user.id, user: this.user,
      isAdmin: this.visible && this.isAdmin, isHead: this.visible && this.isHead,
      isStorekeeper: this.hasProfileContext() && this.isStorekeeper});
  }

  private loadAssignedEmployeeServices(): void {
    const source = this.user;
    const sequence = this.profileContextSequence;
    this.gateway.loadWorkflowPermissions().takeUntil(this.destroy$).subscribe(permissions => {
      if (this.user !== source || sequence !== this.profileContextSequence) { return; }
      this.assignedEmployeeServices = !!permissions && permissions.canUseAssignedEmployeeServices === true;
    }, () => { if (this.user === source && sequence === this.profileContextSequence) { this.assignedEmployeeServices = false; } });
  }

  private setUserState(notFound: boolean, request: boolean): void {
    this.notFound = notFound;
    this.request = request;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-user-profile/view-user-profile.component.ts